import { useState, Dispatch, StateUpdater, useMemo } from 'preact/hooks';
import { PageEditor } from 'client/courses/components/page-builder/page-editor';
import { ThemePanel, type PageTheme } from './theme-panel';
import { ThemeStyles, useThemeFonts } from './theme-styles';
import { Button } from '@components/buttons';
import { IcoRectangleGroup } from '@components/icons';
import { SalesPageState } from 'client/courses/components/page-builder/page-editor';
import { SalesBlockDefinition } from '@course/components/page-builder';
import { useCurrentUser } from 'client/lib/auth';
import { CalendarLinkDisplay } from './calendar-link-display';
import { CalendarMetadata } from './calendar-links';
import {
  CtaPickerContextValue,
  CtaPickerProvider,
} from '@course/components/page-builder/settings-menu';
import { EventTypePane } from '@course/components/page-builder/settings-menu/event-type-pane';

interface CalendarPageEditorProps {
  title: string;
  publicURL: string;
  showShareLink?: boolean;
  state: SalesPageState;
  setState: Dispatch<StateUpdater<SalesPageState>>;
  onReset?(): void;
  blockDefinitions: Record<string, SalesBlockDefinition>;
  isDirty: boolean;
  isConnected: boolean;
  theme?: PageTheme;
  setTheme(theme: PageTheme): void;
}

export function CalendarPageEditor(props: CalendarPageEditorProps) {
  const [isThemePanelOpen, setIsThemePanelOpen] = useState(false);
  const fontsLoaded = useThemeFonts();
  const theme = props.theme || { fonts: [], appliedFonts: {} };
  const user = useCurrentUser();

  // Context value for customizing CTA picker behavior in the calendar page builder.
  // Provides calendar-specific UI for selecting event types and displaying selected links.
  const ctaPickerValue = useMemo<CtaPickerContextValue>(() => {
    if (!user) {
      return {};
    }

    return {
      defaultUrl: { url: `/meet-direct/${user.id}`, description: 'Your public booking page' },
      showPicker: EventTypePane,
      renderLinkUrl: (state) => {
        // Use metadata if available, otherwise fall back to URL parsing for backward compatibility
        if (!state.metadata?.hostId) {
          return null;
        }
        // Cast the generic metadata to our specific CalendarMetadata type
        const calendarMetadata: CalendarMetadata = {
          hostId: state.metadata.hostId,
          eventTypeId: state.metadata.eventTypeId,
        };
        return <CalendarLinkDisplay url={state.url} metadata={calendarMetadata} />;
      },
    };
  }, [user]);

  return (
    <CtaPickerProvider value={ctaPickerValue}>
      <ThemeStyles theme={theme} />
      <PageEditor
        {...props}
        fontsLoaded={fontsLoaded}
        customNavItems={
          <Button
            class="lg:inline-flex flex items-center lg:gap-2 gap-3 lg:text-inherit text-sm lg:hover:bg-gray-200 hover:bg-gray-50 lg:p-2 p-3 lg:px-3 lg:rounded-sm rounded whitespace-nowrap lg:w-auto w-full lg:text-center text-left"
            onClick={() => setIsThemePanelOpen(true)}
          >
            <IcoRectangleGroup class="lg:size-4 w-4 h-4" />
            Theme
          </Button>
        }
      />
      <ThemePanel
        isOpen={isThemePanelOpen}
        onClose={() => setIsThemePanelOpen(false)}
        theme={theme}
        setTheme={props.setTheme}
      />
    </CtaPickerProvider>
  );
}
